import { useEffect, useState } from "react";
import { FormControlLabel, Checkbox } from "@mui/material";
import SurveyResultsButton from "../UserInput/Buttons/SurveyResultsButton";
import { trimStrings, validateAdvisorAppointmentBooking } from "@fhc/shared/index.js";

/**
 * Form for booking an appointment with an advisor.
 * @param {object} props
 * @param {object} props.themeColors The company's color theme.
 * @param {function} props.bookAppointment Callback function to book an appointment.
 */
function BookAppointmentForm({ themeColors, bookAppointment }) {
    const [errors, setErrors] = useState([]);

    const [userInfo, setUserInfo] = useState({
        first_name: "",
        last_name: "",
        email: "",
        place_holder_phone_number: "",
        phone_number: "",
        postal_code: "",
        by_email: false,
        by_phone: false,
        subscribe_to_other_resources: false,
    });

    // The agreed to be contacted checkbox needs to be checked before the form submission button can be clicked
    const [agreeToBeContacted, setAgreeToBeContacted] = useState(false);

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setUserInfo((info) => ({ ...info, phone_number: "+1" + info.place_holder_phone_number.replace("+1", "") }));
    }, [userInfo.place_holder_phone_number]);

    function validateForm(formData) {
        const errors = validateAdvisorAppointmentBooking(formData);

        // This validation is not in the shared validation function because it's only needed on the frontend
        if (!formData.by_email && !formData.by_phone) {
            errors.push({ field: "preferred_contact_method", message: "Preferred contact method is required" });
        }
        if (!agreeToBeContacted) {
            errors.push({
                field: "agree_to_be_contacted",
                message: 'You must check "I agree to be contacted by an advisor"',
            });
        }

        return errors;
    }

    const _bookAppointment = async () => {
        setLoading(true);

        const santizedFormData = trimStrings(userInfo);

        const errors = validateForm(santizedFormData);
        if (errors?.length > 0) {
            setErrors(errors);
            setLoading(false);
            return;
        }

        try {
            bookAppointment(santizedFormData);
        } finally {
            setErrors([]);
            setLoading(false);
        }
    };

    /** @param {"email" | "phone"} method */
    function setPreferredContactMethod(method) {
        setUserInfo((uinfo) => ({
            ...uinfo,
            by_email: method === "email" ? true : false,
            by_phone: method === "phone" ? true : false,
        }));
    }

    function setPhoneNumber(e) {
        let number = e.target.value;
        if (number.substr(0, 1) === "0") {
            number = number.substr(1);
            console.log(number, number.substr(0, 1));
        }
        setUserInfo((uinfo) => ({ ...uinfo, place_holder_phone_number: number }));
    }

    return (
        <>
            {/* Error messages */}
            {errors.length > 0 && (
                <div className="body-text py-2 px-4 rounded-lg bg-red-500 text-white">
                    <ul className="list-disc ml-4">
                        {errors.map((error) => (
                            <li key={`advisor-booking-form-error-${error.field}`} className="text-sm">
                                {error.message}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* This section displays the appointment link */}
            <div className="grid grid-cols-3 gap-4 font-normal">
                {/* First name and last name */}
                <div className="col-span-3 md:col-span-2 w-full flex md:flex-row flex-col gap-4">
                    <input
                        type="text"
                        className="body-text text-[14px] border-none outline-none px-5 py-2.5 rounded-md w-full text-slate-600 font-normal"
                        placeholder="First Name"
                        aria-label="First Name"
                        value={userInfo.first_name}
                        onChange={(e) => setUserInfo((uinfo) => ({ ...uinfo, first_name: e.target.value }))}
                    />
                    <input
                        type="text"
                        className="body-text text-[14px] border-none outline-none px-5 py-2.5 rounded-md w-full text-slate-600 font-normal"
                        placeholder="Last Name"
                        aria-label="Last Name"
                        value={userInfo.last_name}
                        onChange={(e) => setUserInfo((uinfo) => ({ ...uinfo, last_name: e.target.value }))}
                    />
                </div>
                {/* Postal code */}
                <div className="col-span-3 md:col-span-1 w-full">
                    <input
                        type="text"
                        className="body-text text-[14px] border-none outline-none px-5 py-2.5 rounded-md w-full text-slate-600 font-normal"
                        placeholder="Postal Code"
                        aria-label="Postal Code"
                        value={userInfo.postal_code}
                        onChange={(e) => setUserInfo((uinfo) => ({ ...uinfo, postal_code: e.target.value }))}
                    />
                </div>

                {/* Preferred contact method */}
                <div className="col-span-3 flex items-center gap-16">
                    <div className="body-text text-white text-[11px] md:text-base font-normal">Preferred contact method:</div>
                    <div>
                        <FormControlLabel
                            control={<Checkbox size="small" className="text-white" disableRipple />}
                            sx={{
                                "& .MuiFormControlLabel-label": {
                                    color: "#fff",
                                    fontSize: {
                                        md: "16px",
                                        xs: "12px",
                                    },
                                },
                            }}
                            label="By Email"
                            checked={userInfo.by_email}
                            onChange={() => setPreferredContactMethod("email")}
                        />
                        <FormControlLabel
                            control={<Checkbox size="small" className="text-white" disableRipple />}
                            sx={{
                                "& .MuiFormControlLabel-label": {
                                    color: "#fff",
                                    fontSize: {
                                        md: "16px",
                                        xs: "12px",
                                    },
                                },
                            }}
                            label="By Phone"
                            checked={userInfo.by_phone}
                            onChange={(e) => setPreferredContactMethod("phone")}
                        />
                    </div>
                </div>

                {/* Email and phone number */}
                <div className="col-span-3 md:col-span-2 w-full">
                    <input
                        type="text"
                        className="body-text text-[14px] border-none outline-none px-5 py-2.5 rounded-md w-full text-slate-600 font-normal"
                        placeholder="Email"
                        aria-label="Email"
                        value={userInfo.email}
                        onChange={(e) => setUserInfo((uinfo) => ({ ...uinfo, email: e.target.value }))}
                    />
                </div>
                <div className="col-span-3 md:col-span-1 w-full">
                    <div className="flex border-none space-x-2 px-5 py-2.5 pl-2 rounded-md w-full bg-white">
                        <span className="text-slate-600 text-[14px]">+1</span>
                        <div className="border-l border-slate-300"></div>
                        <input
                            type="tel"
                            className="body-text text-[14px] outline-none text-slate-600 font-normal bg-transparent w-full"
                            placeholder="Phone Number"
                            aria-label="Phone Number"
                            value={userInfo.place_holder_phone_number}
                            onChange={setPhoneNumber}
                        />
                    </div>
                </div>

                {/* Agree to be contacted and subscribe checkboxes */}
                <div className="w-full col-span-3 flex flex-col">
                    <FormControlLabel
                        control={<Checkbox size="small" className="text-white" disableRipple />}
                        sx={{
                            "& .MuiFormControlLabel-label": {
                                color: "#fff",
                                fontSize: {
                                    md: 16,
                                    xs: 12,
                                },
                            },
                        }}
                        label="I agree to be contacted by an advisor"
                        checked={agreeToBeContacted}
                        onChange={(event) => setAgreeToBeContacted(event.target.checked)}
                    />

                    <FormControlLabel
                        control={<Checkbox size="small" className="text-white" disableRipple />}
                        sx={{
                            "& .MuiFormControlLabel-label": {
                                color: "#fff",
                                fontSize: {
                                    md: 16,
                                    xs: 12,
                                },
                            },
                        }}
                        label="Subscribe for other resources and communications"
                        checked={userInfo.subscribe_to_other_resources}
                        onChange={(e) => {
                            console.log(e.target.checked);
                            setUserInfo((uinfo) => ({ ...uinfo, subscribe_to_other_resources: e.target.checked }));
                        }}
                    />
                </div>
            </div>

            {/* Book appointment button */}
            <div>
                <SurveyResultsButton handleOnClick={_bookAppointment} colorTheme={themeColors} loading={loading}>
                    Book an appointment
                </SurveyResultsButton>
            </div>
        </>
    );
}

export default BookAppointmentForm;
